// Whale placement relative to the image target reference frame.
// All coordinate math is isolated here; callers just pass XR8 pose data.
//
// Coordinate conventions (XR8 + Three.js share the same right-handed, Y-up space):
//   appConfig.whalePlacement.position: [right, up, back] in meters
//   appConfig.whalePlacement.rotation: Euler [pitch, yaw, roll] in degrees, XYZ order
//
// "back" is negated to -Z so positive values move the whale away from the sign face.

import * as THREE from 'three';
import { appConfig } from '../config/appConfig';
import { getWhaleObject } from './whaleLoader';
import { logger } from '../utils/logger';
import { getMetersToTargetWorldScale } from '../utils/imageTargetScale';

// ── Stabilization window ──────────────────────────────────────────────────────
// Tracks a short window after the whale is first revealed during which
// per-frame placement updates from imageupdated are allowed to settle.

let stabilizationEnd = 0;

/** Call once when the whale becomes visible. Starts the stabilization clock. */
export function startStabilizationWindow(): void {
  stabilizationEnd = Date.now() + appConfig.stabilizationWindowMs;
  logger.log(`Stabilization window opened (${appConfig.stabilizationWindowMs}ms)`);
}

/** True while the post-reveal stabilization window is still active. */
export function isStabilizationActive(): boolean {
  return Date.now() < stabilizationEnd;
}

interface TargetPose {
  position: { x: number; y: number; z: number };
  rotation: { x: number; y: number; z: number; w: number };
  scale?: number;
  scaledWidth?: number;
}

/**
 * Positions and orients the whale relative to the image target's world pose.
 * The whale remains hidden; visibility is controlled separately.
 * Safe to call before the whale has loaded — returns early if not ready.
 */
export function applyWhalePlacement(pose: TargetPose): void {
  const whale = getWhaleObject();
  if (!whale) return;

  const cfg = appConfig.whalePlacement;
  const metersToWorld = getMetersToTargetWorldScale(pose);

  // Target world orientation as a quaternion.
  const targetQuat = new THREE.Quaternion(
    pose.rotation.x,
    pose.rotation.y,
    pose.rotation.z,
    pose.rotation.w,
  );

  // Placement offset in the target's local frame.
  // [right, up, back] → [+X, +Y, -Z] in local space.
  const localOffset = new THREE.Vector3(
    cfg.position[0] * metersToWorld,
    cfg.position[1] * metersToWorld,
    -cfg.position[2] * metersToWorld,
  );

  // Rotate offset into world space and add target world position.
  localOffset.applyQuaternion(targetQuat);
  localOffset.x += pose.position.x;
  localOffset.y += pose.position.y;
  localOffset.z += pose.position.z;

  // Rotation offset (Euler degrees, XYZ) applied on top of target orientation.
  const rotOffset = new THREE.Euler(
    THREE.MathUtils.degToRad(cfg.rotation[0]),
    THREE.MathUtils.degToRad(cfg.rotation[1]),
    THREE.MathUtils.degToRad(cfg.rotation[2]),
    'XYZ',
  );
  const finalQuat = targetQuat.clone().multiply(new THREE.Quaternion().setFromEuler(rotOffset));

  whale.position.copy(localOffset);
  whale.quaternion.copy(finalQuat);

  // Preserve any loader-provided root scale and multiply cfg.scale on top so
  // the config value is always in "natural model units at true-to-life size =
  // 1.0" terms.
  const unitScale = (whale.userData.unitScale as number | undefined) ?? 1;
  whale.scale.setScalar(unitScale * cfg.scale * metersToWorld);

  // logger.log(
  //   isStabilizationActive() ? 'Whale placed (stabilizing)' : 'Whale placed (re-alignment)',
  //   '— position:', whale.position, 'unitScale:', unitScale, 'cfgScale:', cfg.scale,
  // );
}
